import { useEffect, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { ArrowLeftRight, ChevronsUpDown } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { amountProblem, formatMoney } from "./currencyFormat";
import { filterCurrencies } from "./currencySearch";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";

interface Fx { date: string; source: string; stale: boolean; rates: Record<string, number> }

// Used only until the server answers (or if it cannot be reached); always shown as out of date.
const FALLBACK: Fx = { date: "2026-07-26", source: "Built-in snapshot", stale: true, rates: { USD: 1, EUR: 0.878, GBP: 0.7505, JPY: 163.7, CAD: 1.409, AUD: 1.43, CHF: 0.817, CNY: 6.773, INR: 96.4, BRL: 5.08, KRW: 1459.6, MXN: 17.44 } };

const names = (() => { try { return new Intl.DisplayNames(undefined, { type: "currency" }); } catch { return null; } })();
const nameOf = (c: string) => { try { return names?.of(c) ?? c; } catch { return c; } };
const money = formatMoney;
const niceDate = (d: string) => { const [y, m, day] = d.split("-").map(Number); return new Date(y, m - 1, day).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); };

const load = (k: string, d: string) => { try { return localStorage.getItem(k) || d; } catch { return d; } };
const save = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };

function CurrencyPicker({ id, label, value, onChange, currencies, disabledCode }: { id: string; label: string; value: string; onChange: (v: string) => void; currencies: string[]; disabledCode: string }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const list = filterCurrencies(currencies, nameOf, q);
  return (
    <Popover open={open} onOpenChange={o => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <Button id={id} type="button" variant="outline" role="combobox" aria-expanded={open} aria-label={label} className="w-full justify-between font-normal">
          <span className="truncate">{value} · {nameOf(value)}</span><ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[240px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type a code or name" value={q} onValueChange={setQ} aria-label={`Search ${label.toLowerCase()}`} />
          <CommandList>
            <CommandEmpty>No currency matches "{q}".</CommandEmpty>
            {list.map(c => (
              <CommandItem key={c} value={c} disabled={c === disabledCode} onSelect={() => { onChange(c); setOpen(false); setQ(""); }}>
                {c} · {nameOf(c)}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const TOOL_INFO_CONTENT = {
  what: `Converts an amount from one currency to another using the latest daily reference exchange rates (European Central Bank data, refreshed on our server), useful for budgeting a trip, pricing something for an international customer, or just understanding a foreign price.`,
  how: `Enter your amount, select the currency you're converting from and to, and the converted value is calculated automatically.`,
  tip: `Exchange rates shift throughout the day — for anything involving an actual transaction, check the rate your bank or payment processor will actually use, since it may differ slightly from the market rate shown here.`,
};

export default function CurrencyConverter() {
  const [amount, setAmount] = useState("100");
  const [from, setFrom] = useState(() => load("fdp-fx-from", "USD"));
  const [to, setTo] = useState(() => load("fdp-fx-to", "EUR"));
  const [fx, setFx] = useState<Fx>(FALLBACK);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch("/api/fx-rates").then(r => (r.ok ? r.json() : Promise.reject())).then((d: Fx) => { if (alive && d?.rates) { setFx(d); setLoaded(true); } }).catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const currencies = Object.keys(fx.rates).sort();
  const rates = fx.rates;
  useEffect(() => {
    if (!loaded) return;
    if (!rates[from]) setFrom("USD");
    if (!rates[to]) setTo("EUR");
  }, [loaded, rates, from, to]);

  const amountNum = Number(amount);
  const problem = amountProblem(amount);
  const valid = problem === null;
  const { blocked } = useLogToolUsage(valid, 3000);

  const rate = rates[from] && rates[to] ? rates[to] / rates[from] : NaN;
  const converted = valid && Number.isFinite(rate) ? amountNum * rate : NaN;

  const swap = () => { const temp = from; setFrom(to); setTo(temp); save("fdp-fx-from", to); save("fdp-fx-to", temp); };
  const pick = (which: "from" | "to") => (v: string) => { if (which === "from") { setFrom(v); save("fdp-fx-from", v); } else { setTo(v); save("fdp-fx-to", v); } };
  const star = (n: number, c: string) => `${n.toLocaleString(undefined, { maximumSignificantDigits: 6 })} ${c}`;

  return (
    <ToolContainer>
      <div className="max-w-lg mx-auto space-y-6">
        <p className={`text-xs text-center ${fx.stale ? "text-amber-600" : "text-muted-foreground"}`} data-testid="fx-note">
          {fx.stale
            ? `These rates may be out of date (last updated ${niceDate(fx.date)}). Check the current rate before relying on them.`
            : `Rates: ${fx.source}, ${niceDate(fx.date)}. Updated once a day on working days.`}
        </p>
        <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-end">
          <div className="space-y-2">
            <Label htmlFor="fx-from">From</Label>
            <CurrencyPicker id="fx-from" label="Convert from currency" value={from} onChange={pick("from")} currencies={currencies} disabledCode={to} />
            <Input type="number" min={0} step="any" value={amount} onChange={e => setAmount(e.target.value)} aria-label="Amount" aria-invalid={!valid} />
          </div>
          <Button variant="ghost" size="icon" onClick={swap} className="mb-1" aria-label="Swap currencies" title="Swap currencies"><ArrowLeftRight className="h-4 w-4" /></Button>
          <div className="space-y-2">
            <Label htmlFor="fx-to">To</Label>
            <CurrencyPicker id="fx-to" label="Convert to currency" value={to} onChange={pick("to")} currencies={currencies} disabledCode={from} />
            <div className="p-2.5 rounded-md border bg-muted/50 text-lg font-semibold">{blocked || !Number.isFinite(converted) ? "—" : money(converted, to)}</div>
          </div>
        </div>
        {!valid && <p role="alert" className="text-xs text-destructive text-center" data-testid="fx-error">{problem}</p>}
        {Number.isFinite(rate) && (
          <p className="text-sm text-center text-muted-foreground" data-testid="fx-unit">1 {from} = {star(rate, to)} · 1 {to} = {star(1 / rate, from)}</p>
        )}
        {!fx.stale && <p className="text-xs text-muted-foreground text-center" data-testid="fx-coverage">{currencies.length} major currencies (European Central Bank reference rates). Others, such as the Argentine peso or the Nigerian naira, are not covered.</p>}
        <p className="text-xs text-muted-foreground text-center">These are mid-market rates. Banks and cards usually add 1–3%, so you will typically receive a little less.</p>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
